import { formatData } from '@utilities';
/**
 * convertChartToColumnRange
 * @param {Object} chart
 * @param {Object[]} chart.series
 * @return {Object}
 */
const convertChartToColumnRange = ({ series, ...chart }) => {
  const seriesLeftStart = series.map((_, index) => {
    return series
      .slice(0, Math.floor(series.length / 2))
      .reduce((total, { data }) => total + data[index], 0);
  });

  return {
    ...chart,
    series: series.map(({ data, id, name }, index) => {
      return {
        data: data.map((value, index) => {
          const low = seriesLeftStart[index];
          seriesLeftStart[index] += Math.abs(value);

          return {
            high: low + Math.abs(value),
            low: low,
          };
        }),
        id,
        name: index === 0 ? `${name} (LOW)` : index === series.length - 1 ? `${name} (HIGH)` : name,
      };
    }),
  };
};

/**
 * transformColumn - capitalizes Header for FE table purposes
 * @param {Object} column
 * @param {String} column.accessor
 * @param {Number|String?} [column.footer]
 * @param {String?} [column.footer_format]
 * @param {String?} [column.format]
 * @param {String} column.header
 * @param {String?} [column.row_span_by]
 * @param {String?} [column.variant]
 * @return {Object}
 */
const transformColumn = ({
  accessor,
  footer,
  footer_format: footerFormat,
  format,
  header,
  row_span_by: rowSpanBy,
  variant,
}) => ({
  accessor,
  ...(format && {
    Cell: ({ value }) => formatData({ format, value }),
  }),
  ...(footer && {
    Footer: footerFormat ? formatData({ format: footerFormat, value: footer }) : footer,
  }),
  format,
  Header: header,
  rowSpanBy,
  ...(variant && { variant }),
});

/**
 * transformContinuousSumResponses
 * @param {Object} response
 * @param {Object[]} response.data
 * @return {Object[]}
 */
export const transformContinuousSumResponses = ({ data }) => {
  return data.map(({ id, table_count, table_pct, type }) => ({
    id: Number(id),
    tableCount: {
      columns: table_count.columns.map(transformColumn),
      data: table_count.data,
    },
    tablePct: {
      columns: table_pct.columns.map(transformColumn),
      data: table_pct.data,
    },
    type,
  }));
};

/**
 * transformDetails
 * @param {Object} response
 * @param {Number} response.completes
 * @param {Object} response.completes_by_quota_group
 * @param {Number} response.id
 * @param {Number} response.rawdata_row_limit
 * @return {Object}
 */
export const transformDetails = ({
  completes,
  completes_by_quota_group: completesByQuotaGroup,
  id,
  rawdata_row_limit: rawDataRowLimit,
}) => ({
  completes,
  completesByQuotaGroup,
  id,
  rawDataRowLimit,
});

/**
 * transformEssayResponses
 * @param {Object} response
 * @param {Object[]} response.data
 * @return {Object[]}
 */
export const transformEssayResponses = ({ data }) =>
  data.map(({ id, segments, ...question }) => ({
    id: Number(id),
    ...question,
    segments: segments.map(({ table, ...segment }) => ({
      ...segment,
      table: {
        columns: table.columns.map(transformColumn),
        data: table.data,
      },
    })),
  }));

/**
 * transformGlobalFiltersV2
 * @param {Object} filters
 * @param {Object[]} filters.demographic_filters
 * @param {Object[]} filters.quota_groups
 * @return {Object}
 */
export const transformGlobalFiltersV2 = ({ demographic_filters, quota_groups }) => ({
  demographicFilters: demographic_filters.map(({ compare_by: compareBy, id, options, title }) => ({
    compareBy,
    id,
    label: title,
    options,
  })),
  quotaGroups: quota_groups.map(({ name, quota_group_refs: quotaGroupRefs }) => ({
    name,
    quotaGroupRefs,
  })),
});

/**
 * transformGlobalFilters
 * @param {Object} filters
 * @param {Object[]} filters.demographic_filters
 * @param {Object[]} filters.quota_groups
 * @return {Object}
 */
export const transformGlobalFilters = ({ demographic_filters, quota_groups }) => ({
  demographicFilters: demographic_filters.map(({ compare_by: compareBy, id, options, title }) => ({
    compareBy,
    id,
    label: title,
    options,
  })),
  quotaGroups: quota_groups.map(
    ({ instant_survey_group_ref, name, quota_group_refs: quotaGroupRefs }) => ({
      instantSurveyGroupRef: String(instant_survey_group_ref),
      name,
      quotaGroupRefs,
    })
  ),
});

/**
 * transformGridResponses
 * @param {Object} response
 * @param {Object[]} response.data
 * @return {Object[]}
 */
export const transformGridResponses = ({ data }) =>
  data.map(({ id, segments, table_count, table_pct, type }) => {
    return {
      id: Number(id),
      segments: segments.map(({ chart_count, chart_pct, id, name }) => {
        return {
          chartCount: {
            ...chart_count,
            series: chart_count.series.map((series) => ({
              ...series,
              dataLabels: {
                formatter: function () {
                  return formatData({ format: chart_count.format, value: this.y });
                },
              },
            })),
          },
          chartPct: {
            ...chart_pct,
            series: chart_pct.series.map((series) => ({
              ...series,
              dataLabels: {
                formatter: function () {
                  return formatData({ format: chart_pct.format, value: this.y });
                },
              },
            })),
          },
          id,
          name,
        };
      }),
      tableCount: {
        columns: table_count.columns.map(transformColumn),
        data: table_count.data,
      },
      tablePct: {
        columns: table_pct.columns.map(transformColumn),
        data: table_pct.data.map((row) => {
          const formattedColumnCount = table_pct.columns.filter(
            ({ accessor }) =>
              !['option_value', 'compare_by_breakout', 'total_respondents'].includes(accessor)
          ).length;
          const formattedData = table_pct.columns
            // total_respondents is always the last column, count back from there
            .slice(-(formattedColumnCount + 1), -1)
            .map(({ accessor }) => accessor)
            .reduce(
              (acc, accessor) => ({
                ...acc,
                [accessor]: formatData({ format: '0.0%', value: row[accessor] }),
              }),
              {}
            );

          return {
            ...row,
            ...formattedData,
          };
        }),
      },
      type,
    };
  });

/**
 * transformMaxDiffResponses
 * @param {Object} response
 * @param {Object[]} response.data
 * @return {Object[]}
 */
export const transformMaxDiffResponses = ({ data }) => {
  return data.map(({ id, segments, ...question }) => {
    return {
      ...question,
      id: Number(id),
      segments: segments.map(({ chart_count, chart_pct, id, name, table_count, table_pct }) => {
        return {
          chartCount: chart_count,
          chartPct: chart_pct,
          id,
          name,
          tableCount: {
            columns: table_count.columns.map(transformColumn),
            data: table_count.data,
          },
          tablePct: {
            columns: table_pct.columns.map(transformColumn),
            data: table_pct.data,
          },
        };
      }),
    };
  });
};

/**
 * transformRankResponses
 * @param {Object} response
 * @param {Object[]} response.data
 * @return {Object[]}
 */
export const transformRankResponses = ({ data }) =>
  data.map(({ id, segments, ...question }) => {
    return {
      ...question,
      id: Number(id),
      segments: segments.map(({ chart_count, chart_pct, id, name, table_count, table_pct }) => {
        return {
          chartCount: convertChartToColumnRange(chart_count),
          chartPct: convertChartToColumnRange(chart_pct),
          id,
          name,
          tableCount: {
            columns: table_count.columns.map(transformColumn),
            data: table_count.data,
          },
          tablePct: {
            columns: table_pct.columns.map(transformColumn),
            data: table_pct.data.map((row) => {
              const formattedData = table_pct.columns
                .slice(1, -2) // last 2 columns are always numeric (average rank and score)
                .map(({ accessor }) => accessor)
                .reduce(
                  (acc, accessor) => ({
                    ...acc,
                    [accessor]: formatData({ format: '0.0%', value: row[accessor] }),
                  }),
                  {}
                );

              return {
                ...row,
                ...formattedData,
              };
            }),
          },
        };
      }),
    };
  });

/**
 * transformRawData
 * @param {Object} response
 * @param {Object[]} response.data
 * @return {Object[]}
 */
export const transformRawData = ({ data }) =>
  data.map(({ table }) => ({
    table: {
      columns: table.columns.map(transformColumn),
      data: table.data,
    },
  }));

/**
 * transformSurveyResponses
 * @param {Object} response
 * @param {Object[]} response.data
 * @return {Object[]}
 */
export const transformSurveyResponses = ({ data }) =>
  data.map(
    ({ chart_count, chart_pct, id, methodologies, table_count, table_other, table_pct, type }) => {
      return {
        chartCount: {
          ...chart_count,
          series: chart_count.series.map((series) => ({
            ...series,
            dataLabels: {
              formatter: function () {
                return formatData({ format: chart_count.format, value: this.y });
              },
            },
          })),
        },
        chartPct: {
          ...chart_pct,
          series: chart_pct.series.map((series) => ({
            ...series,
            dataLabels: {
              formatter: function () {
                return formatData({ format: chart_pct.format, value: this.y });
              },
            },
          })),
        },
        id: Number(id),
        tableCount: {
          columns: table_count.columns.map(transformColumn),
          data: table_count.data,
        },
        tableOther: {
          columns: table_other?.columns.map(transformColumn),
          data: table_other?.data.map(({ data, id, name: text }) => ({
            id,
            subRows: data.sort(
              ({ text: textA }, { text: textB }) =>
                textB.length - textA.length || textA.localeCompare(textB)
            ),
            text,
          })),
        },
        tablePct: {
          columns: table_pct.columns.map(transformColumn),
          data: table_pct.data.map((row) => {
            const formattedData = table_pct.columns
              .slice(1)
              .map(({ accessor }) => accessor)
              .reduce(
                (acc, accessor) => ({
                  ...acc,
                  [accessor]: formatData({ format: '0.0%', value: row[accessor] }),
                }),
                {}
              );

            return {
              ...row,
              ...formattedData,
            };
          }),
        },
        title: chart_pct?.title || chart_count?.title,
        turfAnalysis: methodologies?.turf
          ? {
              segments: methodologies?.turf?.segments?.map(({ chart, id, name, table }) => ({
                chart: {
                  ...chart,
                  series: chart.series.map((series) => ({
                    ...series,
                    dataLabels: {
                      formatter: function () {
                        return formatData({ format: chart.format, value: this.y });
                      },
                    },
                  })),
                },
                id,
                name,
                table: {
                  columns: table.columns.map(transformColumn),
                  data: table.data,
                },
              })),
            }
          : null,
        type,
      };
    }
  );

/**
 * transformSurveyResponseCounts
 * @param {Object} data
 * @param {Number} data.filtered_responses
 * @param {Number} data.total_responses
 * @return {Object}
 */
export const transformSurveyResponseCounts = (data) => {
  return {
    filteredResponses: data.filtered_responses,
    totalResponses: data.total_responses,
  };
};

/**
 * transformVideoResponses
 * @param {Object} response
 * @param {Object[]} response.data
 * @return {Object[]}
 */
export const transformVideoResponses = ({ data }) => {
  return data.map(({ id, responses, type }) => ({
    id: Number(id),
    // BE refers to this array as responses instead of segments
    segments: responses.map(({ compare_by, quota_group_ref, responses: segmentResponses }) => ({
      compareBy: compare_by,
      name: compare_by,
      quotaGroupRef: quota_group_ref,
      responses: segmentResponses.map(
        ({
          json_transcript,
          response_id,
          text_transcript,
          thumbnail_url,
          user_ref,
          video_url,
        }) => ({
          jsonTranscript: json_transcript,
          responseId: response_id,
          textTranscript: text_transcript,
          thumbnailUrl: thumbnail_url,
          userRef: user_ref,
          videoUrl: video_url,
        })
      ),
    })),
    type,
  }));
};
